<script lang="ts">
  /**
   * Das Geschicktenwetter (amendment 25): the next twelve hours for what the map shows, read off
   * the same weekday and hour in the weeks before.
   *
   * It says what the pattern says and no more: "heiter bis heiß" is the shape of past evenings,
   * and the footnote says so. A place only takes part once enough different people report from
   * it — that rule lives on the server, which is why an empty forecast here is not an error.
   */
  import type { Forecast, ForecastHour, Level } from '$domain/forecast.ts';
  import { request } from './api.ts';
  import { cachedPlace, reverseGeocode } from './place.ts';
  import { describeError } from './session.svelte.ts';

  type Props = {
    bbox: [number, number, number, number] | undefined;
    onclose: () => void;
    onpick: (cell: string) => void;
  };
  const { bbox, onclose, onpick }: Props = $props();

  type Answer = Forecast & { timeZone: string; now: { dow: number; hour: number } };

  const LOOK: Record<Level, { sign: string; word: string }> = {
    0: { sign: '☁️', word: 'ruhig' },
    1: { sign: '🌤️', word: 'heiter' },
    2: { sign: '☀️', word: 'sonnig' },
    3: { sign: '🔥', word: 'heiß' },
  };

  let answer = $state<Answer | undefined>();
  let error = $state('');
  let names = $state<Record<string, string>>({});

  // For the box the map shows: when the panel opens, and again when the map comes to rest
  // somewhere else.
  $effect(() => {
    const box = bbox;
    if (box === undefined) return;
    const timer = setTimeout(() => void load(box), 300);
    return () => clearTimeout(timer);
  });

  async function load(box: [number, number, number, number]) {
    const area = box.map((value) => value.toFixed(4)).join(',');
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      answer = await request<Answer>(
        `/api/forecast?bbox=${area}&tz=${encodeURIComponent(zone)}`,
      );
      error = '';
      for (const place of answer.places) void name(place.cell);
    } catch (cause) {
      error = describeError(cause);
    }
  }

  async function name(cell: string) {
    const known = cachedPlace(cell) ?? (await reverseGeocode(cell));
    if (known !== undefined) names = { ...names, [cell]: known };
  }

  /** "um 23 Uhr", "um 2 Uhr nachts", "morgen um 8 Uhr" — from the reader's own now. */
  function when(entry: { dow: number; hour: number }): string {
    if (entry.dow === answer?.now.dow) return `um ${entry.hour} Uhr`;
    return entry.hour < 6 ? `um ${entry.hour} Uhr nachts` : `morgen um ${entry.hour} Uhr`;
  }

  const busy = $derived((answer?.hours ?? []).filter((entry) => entry.level > 0));
  const headline = $derived.by(() => {
    if (answer === undefined || answer.peak === null) return undefined;
    const levels = busy.map((entry) => entry.level);
    const low = LOOK[Math.min(...levels) as Level].word;
    const high = LOOK[Math.max(...levels) as Level].word;
    const range = low === high ? high : `${low} bis ${high}`;
    return `${range[0]?.toUpperCase()}${range.slice(1)}, am meisten los ${when(answer.peak)}.`;
  });
  const tallest = $derived(Math.max(0, ...(answer?.hours ?? []).map((entry) => entry.expected)));
  const barOf = (entry: ForecastHour) => (tallest > 0 ? entry.expected / tallest : 0);
</script>

<section class="panel" aria-label="Geschicktenwetter">
  <header>
    <h2>🌡️ Geschicktenwetter</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  {#if error}
    <p class="hint error" role="alert">{error}</p>
  {:else if answer === undefined}
    <p class="hint">Einen Moment …</p>
  {:else if answer.basis.cells === 0}
    <p class="today">☁️ Noch kein Wetter für diesen Ausschnitt.</p>
    <p class="hint">
      Hier haben noch nicht genug verschiedene Leute gemeldet, um ein Muster zu sehen. Schieb die
      Karte dorthin, wo mehr los ist – oder melde selbst.
    </p>
  {:else}
    {#if answer.peak === null}
      <p class="today">☁️ Ruhig – zu dieser Zeit war hier bisher nichts los.</p>
    {:else}
      <p class="today"><span class="big">{LOOK[answer.peak.level].sign}</span> {headline}</p>
    {/if}

    <ol class="hours" aria-label="Die nächsten Stunden">
      {#each answer.hours as entry (`${entry.dow}-${entry.hour}`)}
        <li title={`${when(entry)}: ${LOOK[entry.level].word}`}>
          <span class="sign">{LOOK[entry.level].sign}</span>
          <span class="bar"><span style:height="{Math.round(barOf(entry) * 100)}%"></span></span>
          <small>{entry.hour}</small>
        </li>
      {/each}
    </ol>

    {#if answer.places.length > 0}
      <h3>Wo es heiß wird</h3>
      <ul class="places">
        {#each answer.places as place (place.cell)}
          <li>
            <button onclick={() => onpick(place.cell)}>
              <span class="sign">{LOOK[place.level].sign}</span>
              <span class="where">{names[place.cell] ?? 'Eine Wabe in der Nähe'}</span>
              <small>{when(place)}</small>
            </button>
          </li>
        {/each}
      </ul>
    {/if}

    <p class="hint">
      Aus den öffentlichen Meldungen der letzten {answer.basis.weeks} Wochen, am selben Wochentag
      zur selben Stunde: {answer.basis.visits}
      {answer.basis.visits === 1 ? 'Besuch' : 'Besuche'} in {answer.basis.cells}
      {answer.basis.cells === 1 ? 'Wabe' : 'Waben'}. Ein Muster, keine Garantie — und Waben, aus
      denen weniger als drei Leute gemeldet haben, bleiben draußen.
    </p>
  {/if}
</section>

<style>
  .panel {
    position: absolute;
    inset: max(3.5rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom));
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    max-width: 30rem;
    margin-inline: auto;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  h3 {
    margin: 0.4rem 0 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .today {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  .today .big {
    font-size: 1.6rem;
    vertical-align: -0.2rem;
  }

  /* Twelve hours side by side, like the strip of a weather app: the sign, a bar, the hour. */
  .hours {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 0.15rem;
    margin: 0;
    padding: 0.5rem 0.25rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    list-style: none;
  }

  .hours li {
    display: grid;
    justify-items: center;
    gap: 0.2rem;
  }

  .hours .sign {
    font-size: 0.95rem;
  }

  /* A track, filled from the bottom as far as the hour is busy against the busiest of them. */
  .hours .bar {
    display: flex;
    align-items: flex-end;
    width: 0.4rem;
    height: 3rem;
    overflow: hidden;
    border-radius: 0.2rem;
    background: color-mix(in srgb, var(--fg) 8%, transparent);
  }

  .hours .bar span {
    width: 100%;
    background: var(--accent);
  }

  .hours small {
    color: var(--muted);
    font-size: 0.6875rem;
  }

  .places {
    display: grid;
    gap: 0.35rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .places button {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.45rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .places .where {
    overflow: hidden;
    font-size: 0.875rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .places small {
    color: var(--muted);
    font-size: 0.75rem;
  }

  .hint {
    margin: 0;
    color: var(--muted);
    font-size: 0.75rem;
  }

  .error {
    color: var(--accent);
  }
</style>
